import { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import { movies, categories } from "@/mocks/movies";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || "movie");

  const currentCategory = categories.find((c) => c.slug === activeCategory);

  const filteredMovies = useMemo(
    () => movies.filter((m) => m.category === activeCategory),
    [activeCategory]
  );

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    window.history.replaceState(null, "", `/category/${newSlug}`);
  };

  const categorySchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${currentCategory?.name || "分类"} - 红桃影视`,
    description: currentCategory?.description || "",
    url: `https://hongtaomovies.com/category/${activeCategory}`,
    isPartOf: {
      "@type": "WebSite",
      name: "红桃影视",
      url: "https://hongtaomovies.com/",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://hongtaomovies.com/" },
      { "@type": "ListItem", position: 2, name: currentCategory?.name || "分类", item: `https://hongtaomovies.com/category/${activeCategory}` },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(categorySchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <Navbar />
      <div className="pt-16">
        <Breadcrumb />
      </div>

      <main>
        <section className="relative py-12 md:py-16 overflow-hidden">
          {activeCategory === "movie" && (
            <img
              src="https://readdy.ai/api/search-image?query=Cinematic%20movie%20collection%20background%20with%20film%20strips%20and%20warm%20golden%20lighting%2C%20elegant%20theater%20atmosphere%2C%20soft%20focus%20background&width=1800&height=400&seq=category-hero-movie&orientation=landscape"
              alt="电影"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
          )}
          {activeCategory === "tv" && (
            <img
              src="https://readdy.ai/api/search-image?query=Television%20drama%20series%20collection%20background%20with%20warm%20lighting%2C%20cozy%20living%20room%20atmosphere%2C%20soft%20cinematic%20glow&width=1800&height=400&seq=category-hero-tv&orientation=landscape"
              alt="电视剧"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
          )}
          {activeCategory === "variety" && (
            <img
              src="https://readdy.ai/api/search-image?query=Variety%20show%20colorful%20studio%20background%20with%20bright%20stage%20lights%2C%20energetic%20entertainment%20atmosphere%2C%20vibrant%20warm%20colors&width=1800&height=400&seq=category-hero-variety&orientation=landscape"
              alt="综艺"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
          )}
          {activeCategory === "anime" && (
            <img
              src="https://readdy.ai/api/search-image?query=Anime%20style%20colorful%20illustration%20background%20with%20dynamic%20composition%2C%20vibrant%20warm%20colors%2C%20artistic%20anime%20aesthetic%2C%20soft%20glowing%20atmosphere&width=1800&height=400&seq=category-hero-anime&orientation=landscape"
              alt="动漫"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/60 via-foreground-950/40 to-background-50"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-background-50 mb-3">
              <strong>{currentCategory?.name}</strong>
            </h1>
            <p className="text-sm md:text-base text-background-100/80 max-w-md mx-auto">
              {currentCategory?.description}
            </p>
          </div>
        </section>

        <section className="py-8 md:py-12 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                    activeCategory === cat.slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {filteredMovies.map((movie) => (
                <MovieCard key={movie.id} {...movie} />
              ))}
            </div>

            {filteredMovies.length === 0 && (
              <div className="text-center py-20">
                <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4 block"></i>
                <p className="text-foreground-500">暂无相关内容</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}